import { BrandId, brandSpecific } from 'utils/override';

const COMMON_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'live.com',
];

/** `null` switches the feature off for the brand, not just its local list (CMS-1981). */
const LOCAL_EMAIL_DOMAINS = brandSpecific<readonly string[] | null>({
  money101: null,
  moneyhero: ['yahoo.com.hk', 'hotmail.com.hk', 'live.hk'],
  moneymax: null,
  singsaver: ['yahoo.com.sg', 'hotmail.com.sg', 'live.com.sg'],
});

const MAX_DOMAIN_EDIT_DISTANCE = 2;

export function getKnownEmailDomains(brand: BrandId): readonly string[] | null {
  const local = LOCAL_EMAIL_DOMAINS.get(brand);
  return local ? [...COMMON_EMAIL_DOMAINS, ...local] : null;
}

/**
 * The address with its domain swapped for the closest known one; null when the
 * domain is already known or too far from any. The local part is kept as typed.
 */
export function suggestEmailDomain(
  email: string,
  knownDomains: readonly string[] | null,
): string | null {
  if (!knownDomains) return null;

  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;

  const localPart = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (knownDomains.includes(domain)) return null;

  let closest: string | null = null;
  let closestDistance = MAX_DOMAIN_EDIT_DISTANCE + 1;
  for (const candidate of knownDomains) {
    const distance = levenshtein(domain, candidate);
    if (distance < closestDistance) {
      closest = candidate;
      closestDistance = distance;
    }
  }

  return closest ? `${localPart}@${closest}` : null;
}

function levenshtein(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, substitution);
    }
    previous = current;
  }
  return previous[b.length];
}
